﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckSquare } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { tasksService } from '@/services/tasks.service';

interface TasksWidgetProps {
  isAdminTier: boolean;
  isManagerTier: boolean;
}

const todayISO = new Date().toISOString().split('T')[0]!;

type TaskStatus = string;

function taskStatusVariant(status: TaskStatus): 'success' | 'warning' | 'destructive' | 'secondary' | 'outline' {
  if (status === 'completed') return 'success';
  if (status === 'in_progress') return 'default' as 'secondary';
  if (status === 'pending_accept') return 'warning';
  if (status === 'discarded') return 'outline';
  return 'secondary';
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'No due date';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

interface StatChipProps {
  label: string;
  value: number;
  className?: string;
}

function StatChip({ label, value, className = '' }: StatChipProps) {
  return (
    <div className={`rounded-lg px-3 py-2 text-center ${className}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] font-medium opacity-80">{label}</p>
    </div>
  );
}

interface TaskListProps {
  tasks: Array<{ id: string; title: string; display_status: string; periodic_end_date?: string | null }>;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

function TaskList({ tasks, loading, error, onRetry }: TaskListProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-4 text-center">
        <AlertCircle className="h-5 w-5 text-destructive" />
        <p className="text-sm text-muted-foreground">Failed to load tasks</p>
        <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <CheckSquare className="h-7 w-7 opacity-30" />
        <p className="text-sm text-muted-foreground">No tasks to show</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tasks.slice(0, 5).map((t) => (
        <div
          key={t.id}
          className="flex items-start justify-between gap-2 rounded-lg border border-border/40 bg-background/40 px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{t.title}</p>
            <p className="text-xs text-muted-foreground">{formatDate(t.periodic_end_date)}</p>
          </div>
          <Badge variant={taskStatusVariant(t.display_status ?? '')}>
            {(t.display_status ?? '').replace(/_/g, ' ')}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function AdminTasks() {
  const { has } = usePermission();

  const listQ = useQuery({
    queryKey: ['dashboard', 'tasks', 'admin-list'],
    queryFn: () => tasksService.list({ page: 1, limit: 5 }),
    enabled: has(PERMISSIONS.TASK_VIEW_ALL),
  });

  const stoppedQ = useQuery({
    queryKey: ['dashboard', 'tasks', 'stopped-count'],
    queryFn: () => tasksService.stoppedCount(),
    enabled: has(PERMISSIONS.TASK_VIEW_ALL),
  });

  const tasks = listQ.data?.data ?? [];
  const dueToday = tasks.filter((t) => t.periodic_end_date === todayISO).length;
  const overdue = tasks.filter(
    (t) => t.periodic_end_date && t.periodic_end_date < todayISO && t.display_status !== 'completed',
  ).length;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<CheckSquare />}
        title="Tasks Overview"
        action={
          <Link to="/tasks" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        <div className="flex gap-2">
          <StatChip label="Due Today" value={dueToday} className="bg-amber-50 text-amber-700" />
          <StatChip label="Overdue" value={overdue} className="bg-rose-50 text-rose-700" />
          <StatChip
            label="Stopped"
            value={typeof stoppedQ.data === 'number' ? stoppedQ.data : 0}
            className="bg-violet-50 text-violet-700"
          />
        </div>
        <TaskList
          tasks={tasks}
          loading={listQ.isLoading}
          error={listQ.isError}
          onRetry={() => listQ.refetch()}
        />
      </CardContent>
    </Card>
  );
}

function ManagerTasks() {
  const { has } = usePermission();

  const assignedQ = useQuery({
    queryKey: ['dashboard', 'tasks', 'assigned-by-me'],
    queryFn: () => tasksService.assignedByMe(),
    enabled: has(PERMISSIONS.TASK_VIEW_TEAM),
  });

  const tasks = assignedQ.data ?? [];
  const dueToday = tasks.filter((t) => t.periodic_end_date === todayISO).length;
  const overdue = tasks.filter(
    (t) => t.periodic_end_date && t.periodic_end_date < todayISO && t.display_status !== 'completed',
  ).length;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<CheckSquare />}
        title="Team Tasks"
        action={
          <Link to="/tasks" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        <div className="flex gap-2">
          <StatChip label="Due Today" value={dueToday} className="bg-amber-50 text-amber-700" />
          <StatChip label="Overdue" value={overdue} className="bg-rose-50 text-rose-700" />
        </div>
        <TaskList
          tasks={tasks}
          loading={assignedQ.isLoading}
          error={assignedQ.isError}
          onRetry={() => assignedQ.refetch()}
        />
      </CardContent>
    </Card>
  );
}

function UserTasks() {
  const { has } = usePermission();

  const myTasksQ = useQuery({
    queryKey: ['dashboard', 'tasks', 'my-tasks'],
    queryFn: () => tasksService.myTasks(),
    enabled: has(PERMISSIONS.TASK_VIEW_OWN),
  });

  const tasks = myTasksQ.data ?? [];
  const dueToday = tasks.filter((t) => t.periodic_end_date === todayISO).length;
  const overdue = tasks.filter(
    (t) =>
      t.periodic_end_date &&
      t.periodic_end_date < todayISO &&
      t.display_status !== 'completed' &&
      t.display_status !== 'discarded',
  ).length;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<CheckSquare />}
        title="My Tasks"
        action={
          <Link to="/tasks" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        <div className="flex gap-2">
          <StatChip label="Due Today" value={dueToday} className="bg-amber-50 text-amber-700" />
          <StatChip label="Overdue" value={overdue} className="bg-rose-50 text-rose-700" />
        </div>
        <TaskList
          tasks={tasks}
          loading={myTasksQ.isLoading}
          error={myTasksQ.isError}
          onRetry={() => myTasksQ.refetch()}
        />
      </CardContent>
    </Card>
  );
}

export function TasksWidget({ isAdminTier, isManagerTier }: TasksWidgetProps) {
  if (isAdminTier) return <AdminTasks />;
  if (isManagerTier) return <ManagerTasks />;
  return <UserTasks />;
}
